import type { components } from "@/types/api.generated";
import apiClient from "./client";

/**
 * Aliases finos sobre os tipos gerados do OpenAPI (DIAG.1), não tipos à mão.
 *
 * Escrever o contrato à mão criaria um segundo contrato que diverge em silêncio:
 * se o backend mudar o enum de `state`, o `tsc` fica verde e a tela quebra em
 * runtime. Aqui a fonte de verdade continua sendo `api.generated.ts`; estes
 * aliases só dão nome curto para os call sites.
 */
export type DiagnosticSnapshot = components["schemas"]["DiagnosticSnapshotV1"];
export type SectionEnvelope = components["schemas"]["SectionEnvelope"];
export type SectionSource = components["schemas"]["SectionSource"];
export type SectionTruncation = components["schemas"]["SectionTruncation"];

/** Vocabulário de estado do envelope — 7 tokens, fechado pelo backend. */
export type SectionState = SectionEnvelope["state"];

/** Mapa `código da seção → envelope`, como vem do agregador. */
export type DiagnosticSections = DiagnosticSnapshot["sections"];

/** Procedência resolvida da run: `functional`, `architect` e `scope`. */
export type DiagnosticSnapshots = DiagnosticSnapshot["snapshots"];

/**
 * `GET /api/projects/{projectId}/diagnostic` — snapshot consolidado, read-only.
 *
 * Sem try/catch: o erro propaga para o react-query, que já reporta via
 * `QueryCache.onError`. Auth por cookie (o `apiClient` já manda credenciais).
 */
export async function getDiagnostic(projectId: string): Promise<DiagnosticSnapshot> {
  const res = await apiClient.get<DiagnosticSnapshot>(
    `/api/projects/${projectId}/diagnostic`,
  );
  return res.data;
}

/**
 * O índice de aderência ao padrão (EF-FUNC-011): o resumo que o tile e o frame
 * leem de `validation.adherence_index`, e as linhas das três abas do drill-down.
 */
export type AdherenceIndexSummary = components["schemas"]["AdherenceIndexSummary"];
export type AdherenceRow = components["schemas"]["AdherenceRow"];
export type AdherenceStatus = components["schemas"]["AdherenceScopeItem"]["status"];
export type AdherenceItems = components["schemas"]["AdherenceItemsResponse"];
export type AdherenceScopeItem = components["schemas"]["AdherenceScopeItem"];
export type AdherenceDevelopment = components["schemas"]["AdherenceDevelopment"];
export type AdherenceGap = components["schemas"]["AdherenceGap"];

/**
 * `GET /api/projects/{projectId}/adherence-index/items`: scope items, todos os
 * WRICEF e o Registro de GAPs, cada um com a cor e o motivo. Calcula na hora,
 * com as mesmas fontes do resumo do snapshot.
 */
export async function getAdherenceItems(projectId: string): Promise<AdherenceItems> {
  const res = await apiClient.get<AdherenceItems>(
    `/api/projects/${projectId}/adherence-index/items`,
  );
  return res.data;
}

/** O detalhe dos scope items do Diagnóstico (#1506), com os totais do tile. */
export type DiagnosticScopeItems = components["schemas"]["DiagnosticScopeItemsResponse"];
export type DiagnosticScopeItem = components["schemas"]["ScopeItemDetail"];
export type DiagnosticScopeItemsTotals = components["schemas"]["ScopeItemsTotals"];

/**
 * `GET /api/projects/{projectId}/diagnostic/scope-items`: os itens da base que o
 * tile contou, os ativos por padrão e os em decisão com `verdict`. Com
 * `approvalVersion`, lê aquela foto aprovada; sem ela, o servidor resolve a base
 * pela mesma regra do domínio. Versão que não existe é 404, e o erro propaga para
 * o react-query.
 */
export async function getDiagnosticScopeItems(
  projectId: string,
  approvalVersion: number | null,
  verdict?: "activate" | "decision",
): Promise<DiagnosticScopeItems> {
  const res = await apiClient.get<DiagnosticScopeItems>(
    `/api/projects/${projectId}/diagnostic/scope-items`,
    {
      params: {
        ...(approvalVersion == null ? {} : { approval_version: approvalVersion }),
        ...(verdict ? { verdict } : {}),
      },
    },
  );
  return res.data;
}
